import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../contexts/AuthContext';

// 작성자 이니셜 + 색상 (CommentsPanel 과 동일 톤)
const AV_COLORS = ['#2383e2', '#e0561d', '#0f9d58', '#a142f4', '#d93025', '#f4b400', '#1abc9c', '#9b59b6'];
function colorFor(id) {
  let h = 0;
  const s = String(id || '');
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AV_COLORS[h % AV_COLORS.length];
}

// 상대 시간 표시 — "방금"/"3분 전"/"2시간 전"/"어제 14:20"/날짜.
function timeLabel(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Math.max(0, Date.now() - d.getTime());
  const m = Math.floor(diff / 60000);
  if (m < 1) return '방금';
  if (m < 60) return `${m}분 전`;
  const h = Math.floor(m / 60);
  const hhmm = d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false });
  // 어제 여부 — 캘린더 날짜 차이로 판단.
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  if (d.getTime() >= startToday) return `${h}시간 전`;
  if (d.getTime() >= startToday - 86400000) return `어제 ${hhmm}`;
  return `${d.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })} ${hhmm}`;
}

export default function VersionHistory({ pageId, onClose, onRestore }) {
  const { membership } = useAuth();
  const orgId = membership?.org_id;

  const [versions, setVersions] = useState([]);
  const [memberMap, setMemberMap] = useState({}); // user_id -> member
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState('');
  const [restoringId, setRestoringId] = useState(null);

  // 멤버 목록(작성자 이름) — CommentsPanel 과 동일 패턴.
  useEffect(() => {
    let active = true;
    if (!orgId) return undefined;
    (async () => {
      const { data } = await supabase
        .schema('workspace').from('members')
        .select('user_id, display_name, avatar_url, org_id')
        .eq('org_id', orgId);
      if (!active) return;
      const map = {};
      (data || []).forEach((m) => { map[m.user_id] = m; });
      setMemberMap(map);
    })();
    return () => { active = false; };
  }, [orgId]);

  const loadVersions = useCallback(async () => {
    if (!pageId) return;
    setLoading(true);
    // 테이블 없음/RLS 등은 error 로 잡아 빈 목록 + 안내 처리(방어적).
    const { data, error } = await supabase
      .schema('workspace').from('page_versions')
      .select('id, page_id, ystate, label, created_by, created_at')
      .eq('page_id', pageId)
      .order('created_at', { ascending: false })
      .limit(50);
    if (error) {
      setErrMsg('버전 기록을 불러오지 못했어요.');
      setVersions([]);
    } else {
      setErrMsg('');
      setVersions(data || []);
    }
    setLoading(false);
  }, [pageId]);

  useEffect(() => { loadVersions(); }, [loadVersions]);

  const handleRestore = useCallback(async (version) => {
    if (restoringId) return;
    // eslint-disable-next-line no-alert
    const ok = window.confirm('이 버전으로 복원하시겠어요? 현재 내용은 버전 기록에 남습니다.');
    if (!ok) return;
    setRestoringId(version.id);
    try {
      await onRestore(version);
      // onRestore 가 reload 하므로 이 아래는 보통 도달하지 않음.
    } catch (e) {
      setRestoringId(null);
      // eslint-disable-next-line no-alert
      window.alert('복원에 실패했어요. 잠시 후 다시 시도해주세요.');
    }
  }, [onRestore, restoringId]);

  return (
    <aside className="cm-panel vh-panel" role="complementary" aria-label="버전 기록">
      <header className="cm-head">
        <span className="cm-head-title">버전 기록</span>
        <button type="button" className="cm-close" onClick={onClose} aria-label="버전 기록 닫기">✕</button>
      </header>

      <div className="cm-list">
        {loading ? (
          <div className="cm-state"><div className="spinner" /></div>
        ) : errMsg ? (
          <div className="cm-state cm-state-err">{errMsg}</div>
        ) : versions.length === 0 ? (
          <div className="cm-state">
            <div className="cm-state-big">🕓</div>
            <div>저장된 버전이 없습니다.</div>
            <div className="muted" style={{ fontSize: 12 }}>편집을 이어가면 일정 간격으로 버전이 자동 저장돼요.</div>
          </div>
        ) : (
          versions.map((v) => {
            const author = memberMap[v.created_by];
            const authorName = author?.display_name || '알 수 없는 사용자';
            return (
              <div className="vh-item" key={v.id}>
                <span className="cm-avatar vh-avatar" style={{ background: colorFor(v.created_by) }}>
                  {authorName.slice(0, 1)}
                </span>
                <div className="vh-body">
                  <div className="vh-meta">
                    <span className="vh-time">{timeLabel(v.created_at)}</span>
                    {v.label ? <span className="vh-label">{v.label}</span> : null}
                  </div>
                  <div className="vh-author">{authorName}</div>
                  <button
                    type="button"
                    className="vh-restore"
                    onClick={() => handleRestore(v)}
                    disabled={!!restoringId}
                  >
                    {restoringId === v.id ? '복원 중…' : '이 버전으로 복원'}
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </aside>
  );
}
